/*
 * The small chart patterns the frozen design actually uses — `124:123` (Platform Health bars),
 * `119:385` (attribution shares), `121:648` (staffing coverage) and the budget meters at `121:1121`.
 *
 * No chart library. These are four shapes, they must theme from the same tokens as everything else,
 * and a 60 kB dependency to draw twelve rectangles would be the largest thing in the bundle.
 *
 * Both of these state their numbers in text. A bar whose only reading is its height is unreadable
 * to a screen reader, unreadable in greyscale, and unreadable to anyone judging 71% against 74% by
 * eye — which is exactly the judgement these screens exist to support. The visual is the summary;
 * the number is the answer.
 */
import type { ReactNode } from 'react';

export type MeterTone = 'neutral' | 'positive' | 'warning' | 'danger';

/**
 * A labelled proportion — one row of the attribution panel, one hour of coverage, one budget line.
 *
 * `value` is what to print; `percent` is only how far to fill. They are separate because the two are
 * often not the same thing: "฿486,200" fills to 74% of a budget, and printing "74%" instead would
 * lose the figure someone came for.
 */
export function MeterRow({ label, value, percent, tone = 'neutral', caption }: {
  label: string;
  value: ReactNode;
  /** 0–100. Clamped, because a burn rate over budget is a real state and must not overflow its track. */
  percent: number;
  tone?: MeterTone;
  caption?: ReactNode;
}) {
  const filled = Math.max(0, Math.min(100, percent));
  const over = percent > 100;
  return (
    <div className="jt-meter" data-tone={tone}>
      <div className="jt-meter-head">
        <span className="jt-meter-label">{label}</span>
        <span className="jt-meter-value">{value}</span>
      </div>
      <div
        className="jt-meter-track"
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="jt-meter-fill" data-over={over || undefined} style={{ width: `${filled}%` }} />
      </div>
      {caption && <p className="jt-meter-caption">{caption}</p>}
    </div>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  /** What to print for this bar — defaults to the number itself. */
  display?: string;
  tone?: MeterTone;
}

/**
 * A column chart for a short, ordered series — a day of GMV, a week of success rate.
 *
 * The bars are decoration over a real table that only a screen reader reads. That is deliberate:
 * a chart is a shape, and the underlying series is the thing a non-visual reader needs, in the
 * order it happened, without inventing an alt text that tries to describe a trend.
 */
export function BarChart({ data, label, caption, max }: {
  data: readonly BarDatum[];
  /** Names the chart, e.g. "GMV รายชั่วโมงวันนี้". */
  label: string;
  caption?: ReactNode;
  /** Override the scale where the series should be read against a target rather than its own peak. */
  max?: number;
}) {
  const peak = max ?? Math.max(1, ...data.map((d) => d.value));
  return (
    <figure className="jt-chart">
      <div className="jt-chart-plot" aria-hidden="true">
        {data.map((datum) => (
          <span key={datum.label} className="jt-chart-col" title={`${datum.label}: ${datum.display ?? datum.value}`}>
            <span
              className="jt-chart-bar"
              data-tone={datum.tone ?? 'neutral'}
              style={{ height: `${Math.max(2, (datum.value / peak) * 100)}%` }}
            />
            <span className="jt-chart-tick">{datum.label}</span>
          </span>
        ))}
      </div>
      <table className="jt-sr-only">
        <caption>{label}</caption>
        <thead><tr><th scope="col">ช่วง</th><th scope="col">ค่า</th></tr></thead>
        <tbody>
          {data.map((datum) => (
            <tr key={datum.label}>
              <th scope="row">{datum.label}</th>
              <td>{datum.display ?? datum.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {caption && <figcaption className="jt-chart-caption">{caption}</figcaption>}
    </figure>
  );
}
